import { useCallback, useEffect, useState } from "react";
import { Theme } from "@mui/material";
import RecordingEntryDisplay from "@/modules/common/components/audio/RecordingEntryDisplay";
import { useAudioPlayback } from "@/modules/common/hooks/audio";
import {
  generateFallbackWaveform,
  generateWaveformFromUrl,
  WAVEFORM_BAR_COUNT,
  WaveformResult,
} from "@/modules/common/utils/audio.utils";
import { RecordingEntry } from "@/modules/common/types/audio.types";
import { ActivityAttachment } from "@/modules/lead-page/dtos/index-v2";

interface ActivityAttachmentsProps {
  attachments: ActivityAttachment[];
  theme: Theme;
  onRefresh?: () => void;
}

const ActivityAttachments = ({
  attachments,
  theme,
  onRefresh,
}: ActivityAttachmentsProps) => {
  const {
    togglePlay,
    isPlaying,
    getCurrentTimeForId,
    getDurationForId,
    isLoading: audioLoading,
    getErrorForId,
    playingId,
    loadedId,
    seekTo,
    playbackRate,
    setPlaybackRate,
  } = useAudioPlayback();

  const [audioMetadataMap, setAudioMetadataMap] = useState<
    Record<string, WaveformResult>
  >({});

  const audioAttachments = attachments.filter((att) =>
    att.mediaType.startsWith("audio/"),
  );

  useEffect(() => {
    const loadWaveforms = async () => {
      for (const attachment of audioAttachments) {
        if (attachment.presignedUrl && !audioMetadataMap[attachment.uid]) {
          const result = await generateWaveformFromUrl(
            attachment.presignedUrl,
            WAVEFORM_BAR_COUNT,
          );
          setAudioMetadataMap((prev) => ({
            ...prev,
            [attachment.uid]: result,
          }));
        }
      }
    };

    if (audioAttachments.length > 0) {
      loadWaveforms();
    }
  }, [audioAttachments.map((a) => a.uid).join(",")]);

  const toRecordingEntry = useCallback(
    (attachment: ActivityAttachment): RecordingEntry => {
      const localMetadata = audioMetadataMap[attachment.uid];
      const playbackDuration = getDurationForId(attachment.uid);

      const duration =
        attachment.metadata?.duration ??
        (playbackDuration > 0
          ? playbackDuration
          : localMetadata?.duration || 0);

      return {
        id: attachment.uid,
        audioBlob: null,
        audioUrl: attachment.presignedUrl,
        duration: duration > 0 ? duration : 0,
        waveformData:
          localMetadata?.waveformData ||
          generateFallbackWaveform(WAVEFORM_BAR_COUNT),
      };
    },
    [getDurationForId, audioMetadataMap],
  );

  if (audioAttachments.length === 0) {
    return null;
  }

  return (
    <>
      {audioAttachments.map((attachment) => {
        const isCurrentPlaying = isPlaying(attachment.uid);
        const isCurrentLoading =
          audioLoading && playingId === null && loadedId !== attachment.uid;
        const audioErrorForId = getErrorForId(attachment.uid);

        return (
          <RecordingEntryDisplay
            isActivity={true}
            key={attachment.uid}
            recording={toRecordingEntry(attachment)}
            isPlaying={isCurrentPlaying}
            currentPlaybackTime={getCurrentTimeForId(attachment.uid)}
            onTogglePlay={() =>
              togglePlay(attachment.uid, attachment.presignedUrl)
            }
            theme={theme}
            isLoading={isCurrentLoading}
            error={audioErrorForId}
            onRefresh={onRefresh}
            onSeek={(time) => seekTo(attachment.uid, time)}
            playbackRate={playbackRate}
            onPlaybackRateChange={setPlaybackRate}
          />
        );
      })}
    </>
  );
};

export default ActivityAttachments;
